import React, { useEffect, useState, useRef } from "react";
import styled, { ThemeProvider } from "styled-components";

import html2pdf from "html2pdf.js";
import moment from "moment";

import { formatPhoneNumber } from '../../../../../Utils/phoneUtils';
import { useTheme } from '../../../../../Context/ThemeContext';
import { useLanguage } from '../../../../../Context/LanguageContext';
import { lightTheme, darkTheme } from '../../../../../Styles/theme';

import FetchModule from "../../../../Share/Network/FetchModule";
import ChannelTalk from "../../../../Share/Network/ChannelTalk";
import TotalModalWrapper from "../../../../Share/Normal/TotalModalWrapper";

export default function TermsViewModal(props) {
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t, languageMode } = useLanguage();
  const {
    termsListItem,
    storeName,
    defaultProvisions,
    memberName,
    memberGender,
    memberPhone,
    memberCountryCode,
    onClickExit,
  } = props;

  const isCompleteAgreement = termsListItem?.agreementStatus === true;

  const [title, setTitle] = useState(termsListItem?.termsTitle ?? '');
  const [includeProvisions, setIncludeProvisions] = useState([]);
  const [signDate, setSignDate] = useState(moment());
  const [userName, setUserName] = useState(memberName ?? '');
  const [userPhone, setUserPhone] = useState(memberPhone ?? '');
  const [userCountryCode, setUserCountryCode] = useState(memberCountryCode ?? '');
  const [userGender, setUserGender] = useState(memberGender === 'male' ? t('memberDetail.male') : t('memberDetail.female'));
  const [signature, setSignature] = useState('');

  // PDF 다운로드 모드 — 다크모드여도 라이트 테마로 출력 //
  const [isPdfMode, setIsPdfMode] = useState(false);

  const viewTheme = isPdfMode ? lightTheme : theme;

  const TermsRef = useRef(null);
  const TitleBoxRef = useRef(null);
  const TitleRef = useRef(null);
  const MemberInfoRef = useRef(null);
  const SubTitleTextRef = useRef(null);
  const IncludeProvisionsRef = useRef([]);
  const IncludeProvisionsTitleRef = useRef([]);
  const IncludeProvisionsTextRef = useRef([]);
  const DefaultProvisionsRef = useRef([]);
  const DefaultProvisionsTitleRef = useRef([]);
  const DefaultProvisionsTextRef = useRef([]);
  const DivisionLineRef = useRef(null);
  const SignWrapperRef = useRef(null);
  const StoreNameRef = useRef(null);

  // Channel Talk Commponent Building //
  useEffect(() => {
    ChannelTalk.shutdown();

    return () => {
      ChannelTalk.boot({"pluginKey": "d2f4dfce-95e8-498e-aed3-84804b9d3772"});
    }
  }, [])

  // 약관 본문/서명 데이터 로드 — 본문: 현재본 / 서명 정보: 서명완료 항목만 //
  useEffect(() => {
    if (termsListItem === undefined) {
      setTitle('');
      setIncludeProvisions([]);
      return;
    }

    setTitle(termsListItem.termsTitle ?? '');

    const fetchModule = new FetchModule();

    // 약관 본문 로드 — 현재 약관 기준 //
    (
      async () => {
        const responseData = await fetchModule.getDataVersion2('terms/' + termsListItem.termsId, 'GET');

        if (responseData.status === 200) {
          const data = responseData.data;

          if (data.title) setTitle(data.title);
          setIncludeProvisions(Array.isArray(data.provisions) ? data.provisions : []);
        }
      }
    )();

    // 서명 정보 로드 — 서명완료 항목만 //
    if (termsListItem.agreementStatus === true && termsListItem.termsAgreementId !== undefined && termsListItem.termsAgreementId !== null) {
      const requestData = {
        termsAgreementId: termsListItem.termsAgreementId
      };

      (
        async () => {
          const responseData = await fetchModule.postDataVersion2('terms/agreement', 'POST', requestData);

          if (responseData.status === 200) {
            const data = responseData.data;

            setSignDate(moment(data.createdDate ?? termsListItem.signedDate ?? moment()));
            setUserName(data.name ?? '');
            setUserPhone(data.phone ?? '');
            setUserCountryCode(data.countryCode ?? '');
            setUserGender(data.gender === 'male' ? t('memberDetail.male') : t('memberDetail.female'));

            if (data.signature) {
              convertImageToBase64(data.signature)
                .then((base64Image) => setSignature(base64Image))
                .catch((_error) => {});
            }
          }
        }
      )();
    }
  }, [termsListItem])

  // PDF 모드 진입 시 라이트 테마로 렌더된 약관을 PDF로 저장 //
  useEffect(() => {
    if (!isPdfMode) return;

    const totalHeight = TermsRef.current?.offsetHeight;
    const fileName = signDate.format('YY.MM.DD') + ' ' + storeName + '-' + title + '(' + userName + ').pdf';

    let pageBreakMode = [];

    if (totalHeight >= 1093) pageBreakMode = ['avoid-all', 'css', 'legacy'];

    const options = {
      margin: [4, 4, 4, 4], // 상, 좌, 하, 우 여백 설정
      filename: fileName, // 파일 이름
      image: { type: 'jpeg', quality: 1 }, // 이미지 품질 설정
      html2canvas: { scale: 2, dpi: 300, letterRendering: true }, // HTML 캔버스를 300dpi로 렌더링
      jsPDF: {
        unit: 'mm',  // 단위 설정 (mm)
        format: 'a4', // 페이지 크기 설정 (A4)
        orientation: 'portrait', // 세로 모드 (landscape로 설정하면 가로)
      },
      pagebreak: { mode: pageBreakMode }
    };

    html2pdf()
      .from(TermsRef.current)
      .set(options)
      .save()
      .then(() => setIsPdfMode(false));
  }, [isPdfMode])


  const convertImageToBase64 = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'Anonymous';  // CORS 설정
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataURL = canvas.toDataURL('image/png');
        resolve(dataURL);
      };
      img.onerror = reject;
      img.src = imageUrl;
    });
  };

  // 전화번호 뷰 정리 //
  function selectPhoneView(phone, countryCode) {
    if (!phone || phone === '') return t('memberDetail.noPhone');
    return formatPhoneNumber(phone, countryCode, languageMode) || t('memberDetail.noPhone');
  }

  // 약관 다운로드 클릭시 — 라이트 테마(PDF 모드)로 전환 후 출력 //
  function onClickDownloadTerms() {
    setIsPdfMode(true);
  }

  return (
    <TotalModalWrapper backgroundColor='rgba(0, 0, 0, 0.5)'>
      <Modal>
        <InfoWrapper>
          <TopLineWrapper>
            <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={theme.text} hover='default'>{t('memberDetail.termsModal.termsViewTitle')}</Text>
            <TopLineButtonWrapper>
              <DeleteButton onClick={onClickExit}>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text} hover='pointer'>{t('memberDetail.close')}</Text>
              </DeleteButton>
              <DownloadBox onClick={onClickDownloadTerms}>
                <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.subBackground} hover='pointer'>{t('memberDetail.termsModal.termsDownload')}</Text>
              </DownloadBox>
            </TopLineButtonWrapper>
          </TopLineWrapper>
          <ComponentView>

              <TermsScroll>
                <ThemeProvider theme={viewTheme}>
                  <TermsWrapper ref={TermsRef}>
                    <TermsTitleBoxWrapper ref={TitleBoxRef}>
                      <Text fontFamily='NotoSansKR-Bold' fontSize={16} color={viewTheme.text} hover='default'>{title}</Text>
                    </TermsTitleBoxWrapper>
                    <TermsTitle ref={TitleRef}>
                      <Text fontFamily='NotoSansKR-Medium' fontSize={16} color={viewTheme.text} hover='default'>{t('memberDetail.termsModal.termsContractTitle').replace('{storeName}', '[' + storeName + ']')}</Text>
                    </TermsTitle>

                    <TermsInfoWrapper>
                      <TotalMemberInfoWrapper ref={MemberInfoRef}>
                        <DivisionLine style={{ margin: '10px 0px 14px 0px', padding: '0px' }} width='100%' backgroundColor={viewTheme.line} />
                        <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{t('memberDetail.termsModal.termsMemberInfo')}</Text>
                        <MemberInfoWrapper>
                          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{t('memberDetail.termsModal.termsLabelName')}</Text>
                          <Text style={{marginRight: '16px'}} fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{' : ' + userName}</Text>
                          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{t('memberDetail.termsModal.termsLabelGender')}</Text>
                          <Text style={{marginRight: '16px'}} fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{' : ' + userGender}</Text>
                          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{t('memberDetail.termsModal.termsLabelPhone')}</Text>
                          <Text style={{marginRight: '16px'}} fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{' : ' + selectPhoneView(userPhone, userCountryCode)}</Text>
                        </MemberInfoWrapper>

                        <DivisionLine style={{ margin: '10px 0px 14px 0px', padding: '0px' }} width='100%' backgroundColor={viewTheme.line} />
                      </TotalMemberInfoWrapper>

                      <ProvisionInfoWrapper>
                        <Text ref={SubTitleTextRef} fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{t('memberDetail.termsModal.termsOfUse')}</Text>
                        {
                          includeProvisions.map((data, index) => (
                            <ProvisionInfoWrapper
                              key={'IncludeProvision-' + index}
                              ref={(element) => (IncludeProvisionsRef.current[index] = element)}>
                              <Text ref={(element) => (IncludeProvisionsTitleRef.current[index] = element)} fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{data.title}</Text>
                              <PreText ref={(element) => (IncludeProvisionsTextRef.current[index] = element)} fontFamily='NotoSansKR-Regular' fontSize={14} color={viewTheme.text} hover='default'>{data.text}</PreText>
                            </ProvisionInfoWrapper>
                          ))
                        }
                        {
                          defaultProvisions.map((data, index) => (
                            <ProvisionInfoWrapper
                              key={'DefaultProvision-' + index}
                              ref={(element) => (DefaultProvisionsRef.current[index] = element)}>
                              <Text ref={(element) => (DefaultProvisionsTitleRef.current[index] = element)} fontFamily='NotoSansKR-Medium' fontSize={14} color={viewTheme.text} hover='default'>{data.title}</Text>
                              <PreText ref={(element) => (DefaultProvisionsTextRef.current[index] = element)} fontFamily='NotoSansKR-Regular' fontSize={14} color={viewTheme.text} hover='default'>{data.text.replaceAll('{storeName}', storeName)}</PreText>
                            </ProvisionInfoWrapper>
                          ))
                        }
                      </ProvisionInfoWrapper>

                      <DivisionLine ref={DivisionLineRef} style={{ margin: '20px 0px 30px 0px', padding: '0px' }} width='100%' backgroundColor={viewTheme.line} />

                      <SignWrapper ref={SignWrapperRef}>
                        <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={viewTheme.text} hover='default'>{signDate.format(t('memberDetail.termsModal.termsDateFormat'))}</Text>
                        <SignBoxWrapper>
                          {
                            isCompleteAgreement ?
                              <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={viewTheme.text} hover='default'>{userName}</Text>
                              :
                              <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={viewTheme.text} hover='default'>{t('memberDetail.termsModal.termsMemberName')}</Text>
                          }
                          <SignBox>
                            <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={viewTheme.text} hover='default'>{t('memberDetail.termsModal.termsSignature')}</Text>
                            {
                              isCompleteAgreement &&
                              <SignatureBoxWrapper>
                                {
                                  isPdfMode ?
                                    <SignatureImage src={signature}/>
                                    :
                                    <SignatureBox $signature={signature}/>
                                }
                              </SignatureBoxWrapper>
                            }
                          </SignBox>
                        </SignBoxWrapper>
                      </SignWrapper>
                      <StoreNameWrapper ref={StoreNameRef}>
                        <Text fontFamily='NotoSansKR-Bold' fontSize={14} color={viewTheme.text} hover='default'>{storeName}</Text>
                      </StoreNameWrapper>
                    </TermsInfoWrapper>
                  </TermsWrapper>
                </ThemeProvider>
              </TermsScroll>

          </ComponentView>
        </InfoWrapper>
      </Modal>
    </TotalModalWrapper>
  )
}


const Modal = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 1034px;
  min-height: 320px;
  height: 86vh;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  padding: 0 0 20px 0;

  width: calc(100% - 40px);
  height: calc(100% - 20px);
`;

const TopLineWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  margin-top: 10px;

  width: 100%;
`;

const TopLineButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 15px;
`;

const DeleteButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 62px;
  height: 40px;

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const DownloadBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 0px 16px;

  width: 171px;
  height: 40px;

  border-radius: 5px;
  background-color: ${props => props.theme.primary};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ComponentView = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: flex-start;

  margin-top: 20px;

  width: 100%;
  height: calc(100% - 60px);
`;

//const TermsView = styled.div`
//  display: flex;
//  flex-direction: column;

//  justify-content: flex-start;
//  align-items: flex-start;

//  width: 100%;
//  height: calc(100% - 40px);

//  border-radius: 10px;
//  background-color: ${props => props.theme.background};
//  background: green;
//`;

const TermsScroll = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
  height: calc(100% - 40px);

  background-color: ${props => props.theme.subBackground};

  overflow-x: hidden;
  overflow-y: scroll;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    box-shadow: inset 0 0 10px 20px ${props => props.theme.primary};
    border: 1px solid transparent;
    height: 25px;
    border-radius: 200px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-track-piece {
    background-color: transparent;
  }
`;

const TermsWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 10px;

  width: calc(100% - 30px);

  border-radius: 10px;
  background-color: ${props => props.theme.buttonFill};
`;

const TermsTitleBoxWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: calc(100% - 4px);
  height: 36px;

  border: 1px solid ${props => props.theme.line};
  border-radius: 5px;
  background-color: ${props => props.theme.subBackground};
`;

const TermsTitle = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 8px 10px;
  margin-top: 10px;

  width: calc(100% - 20px);
`;

const TermsInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  padding: 0px 10px;

  width: calc(100% - 20px);
`;

const TotalMemberInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: flex-start;

  width: 100%;
`;

const MemberInfoWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: flex-start;

  margin-top: 18px;

  width: 100%;

  gap: 4px;
`;

const DivisionLine = styled.div`
  width: ${props => props.width};
  height: 2px;

  opacity: 0.1;

  background-color: ${props => props.backgroundColor};
`;

const ProvisionInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;

  flex-wrap: wrap;

  justify-content: flex-start;
  align-items: flex-start;

  gap: 24px;
`;

const SignWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const SignBoxWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 20px;
`;

const SignBox = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 146px;
  height: 36px;
`;

const SignatureBoxWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 146px;
  height: 60px;
`;

const SignatureImage = styled.img`
  width: 100%;
  height: 100%;
`;

const SignatureBox = styled.div`
  width: 100%;
  height: 100%;

  background-color: ${props => props.theme.text};

  -webkit-mask-image: url(${props => props.$signature});
  mask-image: url(${props => props.$signature});

  -webkit-mask-repeat: no-repeat;
  mask-repeat: no-repeat;

  -webkit-mask-position: center;
  mask-position: center;

  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
`;

const StoreNameWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  padding: 20px 0px 60px 0px;

  width: 100%;
`;

const PreText = styled.pre`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0 0 0 0;

  white-space: pre-wrap;

  color: ${props => props.color};

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.hover};
    }
  }

  transition: all 0.3s ease-in-out;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  margin: 0 0 0 0;

  color: ${props => props.color};

  @media (hover: hover) {
    &:hover {
      cursor: ${props => props.hover};
    }
  }

  transition: all 0.3s ease-in-out;
`;